import { DateTime } from "luxon";
import moment from "moment";

import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { BilledServiceItem } from "@/Billing/Api/Inbox/Dto/Response/BillingInboxItem";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IndividualDTO } from "@/Communications/Models/MessageOrTaskItem";
import { MessageOrTaskNote } from "@/Patient/Models/History/MessageOrTaskNotes";
import { PatientCommunicationsItem } from "@/Patient/Models/History/PatientCommunicationsItem";
import { PatientCommunicationsListItem } from "@/Patient/Models/History/PatientCommunicationsListItem";
import { PatientMessageItem } from "@/Patient/Models/History/PatientMessageItem";
import { PatientMessageListItem } from "@/Patient/Models/History/PatientMessageListItem";
import { PatientPrivateBillItem, PatientPrivateBillItemResponse, PatientSelectedBillInfoResponse } from "@/Patient/Models/History/PatientPrivateBillItem";
import { PatientPrivateBillListItem } from "@/Patient/Models/History/PatientPrivateBillListItem";
import { PatientRecallsItem } from "@/Patient/Models/History/PatientRecallsItem";
import { PatientRecallsListItem } from "@/Patient/Models/History/PatientRecallsListItem";
import { PatientTaskItem } from "@/Patient/Models/History/PatientTaskItem";
import { PatientTaskListItem } from "@/Patient/Models/History/PatientTaskListItem";
import { RecallCommunicationItem } from "@/Patient/Models/History/RecallCommunicationItem";
import { RecallCommunicationListItem } from "@/Patient/Models/History/RecallCommunicationListItem";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientHistoryResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetAppointment, this.LoadPatientAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_Appointment_ChangeStatus, this.FinishAppointmentSave);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetMessage, this.LoadPatientMessage);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetTasks, this.LoadPatientTask);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetCommunications, this.LoadPatientCommunications);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_CreateCommunication, this.FinishCreateNewCommunication);
        this.RegisterOperation(ServerOperations.Server.Myle_CallCenter_CallCommunicationUpdate, this.FinishCallCommunication);
        this.RegisterOperation(ServerOperations.Server.Myle_CallCenter_CallCommunicationAdd, this.FinishCallCommunication);
        this.RegisterOperation(ServerOperations.Server.Myle_CallCenter_CallCommunicationDelete, this.FinishCallCommunication);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetRecalls, this.LoadPatientRecalls);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetRecallCommunication, this.LoadRecallCommunication);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_MarkSelectedRecallAsCompleted, this.FinishMarkRecallAsCompleted);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_InvalidatePrivateBill, this.CompleteInvalidationBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetPatientPrivateBill, this.LoadPatientPrivateBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GeneratePatientPrivateBillPDF, this.GeneratePatientPrivateBillPDF);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, this.GetSelectedBillInformation, SubServerOperations.PrivateBill.LoadInfoFromHistory);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, this.FinalizePrivateBillSave, SubServerOperations.PrivateBill.Save);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, this.FinalizePrivateBillSave_PortalWorkFlow, SubServerOperations.PrivateBill.SaveWithPortalSyncWorkFlow);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GeneratePatientAppointmentHistoryPDF, this.GenerateAppointmentsHistoryPDF);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_PrivateBill_LoadCostCenterForMexico, this.LoadCostCentersForMexico);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_GenerateAppointmentConfirmationPDF, this.showPdfPrintPreview);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_LoadMessageOrTaskNote, this.LoadTaskNotes, SubServerOperations.PatientHistory.LoadTaskNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_LoadMessageOrTaskNote, this.LoadMessageNotes, SubServerOperations.PatientHistory.LoadMessageNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_SetTaskStatus, this.Myle_SetTaskStatus);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_DeleteSelectedBill, this.FinishCloseAndDeleteSelectedBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_CloseSelectedBill, this.FinishCloseAndDeleteSelectedBill);

        // New Portal Message
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, this.loadSharedItem, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, this.loadSharedItem, SubServerOperations.PortalLinkedItemStatus.Communication);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.sendPrivateBillToPortal, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.sendCommunicationToPortal, SubServerOperations.PortalLinkedItemStatus.Communication);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.revokePrivateBill, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.revokeCommunication, SubServerOperations.PortalLinkedItemStatus.Communication);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SingleTask, this.getSingleTask);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SingleMessage, this.getSingleMessage);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SingleRecall, this.getSingleRecall);

        this.RegisterOperation(ServerOperations.Server.Myle_AppointmentCertificate_GeneratePdf, this.showPdfPrintPreview);
    }

    private Myle_SetTaskStatus(response: ServerEvent, store: PatientStore): void {
        var taskModel = store.PatientHistoryTasksModels[response.PatientId];

        store.PatientHistoryTasksModels[response.PatientId] = taskModel;
        store.ActionsHistory.LoadPatientTask(response.PatientId, taskModel.get("SearchText"), taskModel.get("DateFrom"), taskModel.get("DateTo"));
    }

    private LoadCostCentersForMexico(response: ServerEvent, store: PatientStore): void {
        var responseModel: PrivateBillCostCenter[] = response.Data.CostCenters;

        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        if (responseModel != null) {
            privateBillModel = privateBillModel.set("CostCenterList", Immutable.List<PrivateBillCostCenter>(responseModel));
        }
        else
            privateBillModel = privateBillModel.set("CostCenterList", Immutable.List<PrivateBillCostCenter>([]));

        if (responseModel != null && responseModel.length > 0) {
            if (!IsNullOrWhiteSpace(sessionStore.cachedPrivateBillCostCenterId))
                privateBillModel = privateBillModel.set("SelectedCostCenterId", sessionStore.cachedPrivateBillCostCenterId);
            else
                privateBillModel = privateBillModel.set("SelectedCostCenterId", responseModel[0].ID);
        }

        if (!privateBillModel.get("ShowDetailsOrNewBill")) {
            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
        }
        if (!privateBillModel.get("IsNewBill")) {
            privateBillModel = privateBillModel.set("IsNewBill", true);
        }
        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
    }

    private LoadMessageNotes(response: ServerEvent, store: PatientStore): void {
        var responseModel: MessageOrTaskNote[] = response.Data.MessageOrTaskNotesList;

        var messageModel = store.PatientHistoryMessagesModels[response.PatientId];
        if (responseModel && responseModel.length > 0)
            messageModel = messageModel.set("Notes", Immutable.List(responseModel));
        else
            messageModel = messageModel.set("Notes", Immutable.List([]));

        store.PatientHistoryMessagesModels[response.PatientId] = messageModel;
    }

    private LoadTaskNotes(response: ServerEvent, store: PatientStore): void {
        var responseModel: MessageOrTaskNote[] = response.Data.MessageOrTaskNotesList;

        var taskModel = store.PatientHistoryTasksModels[response.PatientId];
        if (responseModel && responseModel.length > 0)
            taskModel = taskModel.set("Notes", Immutable.List(responseModel));
        else
            taskModel = taskModel.set("Notes", Immutable.List([]));

        store.PatientHistoryTasksModels[response.PatientId] = taskModel;
    }

    private LoadPatientAppointment(response: ServerEvent, store: PatientStore): void {
        if (response.hasErrors() || response.Data.AppointmentList == null) {
            let appointmentModel = store.PatientHistoryAppointmentsModels[response.PatientId];
            appointmentModel = appointmentModel
                .set("AppointmentList", Immutable.List<PatientAppointmentListItem>([]))
                .set("AppointmentListLoaded", false);
            store.PatientHistoryAppointmentsModels[response.PatientId] = appointmentModel;
            return;
        }

        const appointmentList = PatientHelper.getAppointmentListFromResponse(response.Data.AppointmentList);

        let appointmentModel = store.PatientHistoryAppointmentsModels[response.PatientId];
        appointmentModel = appointmentModel
            .set("AppointmentList", Immutable.List<PatientAppointmentListItem>(appointmentList))
            .set("AppointmentListLoaded", true);
        store.PatientHistoryAppointmentsModels[response.PatientId] = appointmentModel;
    }

    private FinishAppointmentSave(response: ServerEvent, store: PatientStore): void {
        let appointmentModel = store.PatientHistoryAppointmentsModels[response.PatientId];
        if (response.Data.HasPassed) {
            appointmentModel = appointmentModel
                .set("StateName", cacheStore.GetLocalizedValueByLookupId(appointmentModel.get("SelectedAppointmentStateId")))
                .set("StateId", appointmentModel.get("SelectedAppointmentStateId"))
                .set("IsComboAppointmentChangePopupVisible", false)
                .set("OpenModifyPopup", false)
                .set("Notes", response.Data.Notes);

            store.PatientHistoryAppointmentsModels[response.PatientId] = appointmentModel;
        }

        if (appointmentModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientAppointment(store.PatientUIModels[response.PatientId].get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
        else
            store.ActionsHistory.LoadPatientAppointment(store.PatientUIModels[response.PatientId].get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), null, null, "");
    }

    private LoadPatientMessage(response: ServerEvent, store: PatientStore): void {
        if (response.Data.MessageList) {
            var ListItems = response.Data.MessageList.map(n => {
                var itemModel: PatientMessageItem = new PatientMessageItem();

                itemModel.IndividualMessageId = n.IndividualMessageId;

                var employee = cacheStore.GetEmployeeByContactIdOrEmpty(n.FromEmployee);
                var employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
                itemModel.FromEmployee = employeeTitle + " " + employee.LastName + ", " + employee.FirstName;

                itemModel.SendDate = n.SendDate;
                itemModel.Subject = n.Subject;
                itemModel.Body = n.Body;

                itemModel.ToEmployees = n.ToEmployees;

                var listItem: PatientMessageListItem = new PatientMessageListItem(itemModel);
                return listItem;
            });

            var messageModel = store.PatientHistoryMessagesModels[response.PatientId];
            messageModel = messageModel.set("MessageList", Immutable.List<PatientMessageListItem>(ListItems));
            store.PatientHistoryMessagesModels[response.PatientId] = messageModel;
        }
        else {
            var messageModel = store.PatientHistoryMessagesModels[response.PatientId];
            messageModel = messageModel.set("MessageList", Immutable.List<PatientMessageListItem>([]));
            store.PatientHistoryMessagesModels[response.PatientId] = messageModel;
        }
    }

    private LoadPatientTask(response: ServerEvent, store: PatientStore): void {
        if (response.Data.TaskList) {
            var listItems = response.Data.TaskList.map(n => {
                var itemModel: PatientTaskItem = new PatientTaskItem();

                itemModel.IndividualMessageId = n.IndividualMessageId;

                var employee = cacheStore.GetEmployeeByContactIdOrEmpty(n.FromEmployee);
                var employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);

                itemModel.FromEmployee = employeeTitle + " " + employee.LastName + ", " + employee.FirstName;

                itemModel.SendDate = n.SendDate;
                itemModel.Subject = n.Subject;
                itemModel.Body = n.Body;
                itemModel.PriorityLookupId = n.PriorityLookupId;
                itemModel.StartDate = n.StartDate;
                itemModel.DueDate = n.DueDate;
                itemModel.IsGroupTask = n.IsGroupTask;

                var priority = cacheStore.GetLocalizedValueByLookupId(n.PriorityLookupId);
                var type = cacheStore.GetLocalizedValueByLookupId(n.TypeTaskLookupId);

                if (priority)
                    itemModel.Priority = priority;

                if (type)
                    itemModel.Type = type;

                var listItem: PatientTaskListItem = new PatientTaskListItem(itemModel);

                listItem.GenericTaskStatusId = n.OverallTaskStatusLookupId;

                return listItem;
            });

            var taskModel = store.PatientHistoryTasksModels[response.PatientId];
            taskModel = taskModel.set("TaskList", Immutable.List<PatientTaskListItem>(listItems));
            store.PatientHistoryTasksModels[response.PatientId] = taskModel;

            if (taskModel.get("ShowDetails") == true && taskModel.get("IM_ID") != null) {
                var newTask = taskModel.get("TaskList").filter(x => x.Model.IndividualMessageId == taskModel.get("IM_ID")).get(0);
                if (newTask != null) {
                    window.setTimeout(() => {
                        var operation = new LocalEvent(LocalPatientOperations.History.Task_ShowSelectedTask);
                        operation.AddItem("task", newTask);
                        LocalEventDispatcher.dispatch(operation);
                    }, 50);
                }
            }
        }
        else {
            var taskModel = store.PatientHistoryTasksModels[response.PatientId];
            taskModel = taskModel.set("TaskList", Immutable.List<PatientTaskListItem>([]));
            store.PatientHistoryTasksModels[response.PatientId] = taskModel;
        }
    }

    private LoadPatientCommunications(response: ServerEvent, store: PatientStore): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        if (response.Data.CommunicationsList) {
            let listItems = response.Data.CommunicationsList.map(n => {
                let itemModel: PatientCommunicationsItem = new PatientCommunicationsItem();

                let employee = cacheStore.GetEmployeeByContactFacetId(n.EmployeeId);
                let employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
                itemModel.Id = n.Id;
                itemModel.FromEmployee = employeeTitle + " " + employee.LastName + ", " + employee.FirstName;
                itemModel.SharedItem = n.SharedItem;
                itemModel.Note = n.Note;
                itemModel.CallId = n.CallId;
                itemModel.ProviderId = n.ProviderId;
                itemModel.CallReasons = n.CallReasons;
                itemModel.CallStart = n.CallStart;
                itemModel.CallEnd = n.CallEnd;
                itemModel.IsCallOutgoing = n.IsCallOutgoing;
                itemModel.FollowUpId = n.FollowUpLookupId;
                itemModel.FollowUpString = cacheStore.GetLocalizedValueByLookupId(n.FollowUpLookupId);
                itemModel.CreationDate = DateTime.fromISO(n.CreationDate);

                let listItem: PatientCommunicationsListItem = new PatientCommunicationsListItem(itemModel);
                return listItem;
            });

            communicationsModel = communicationsModel.set("CommunicationsList", Immutable.List<PatientCommunicationsListItem>(listItems));
            communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", false);
            communicationsModel = communicationsModel.set("PortalLinkedItemModel", Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()));
        }
        else {
            communicationsModel = communicationsModel.set("CommunicationsList", Immutable.List<PatientCommunicationsListItem>([]));
        }

        let patient = store.PatientModels[response.PatientId];
        if (patient && patient.IndividualResponse)
            communicationsModel = communicationsModel.set("RegistrationStatus", patient.IndividualResponse.PortalSyncState);

        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;
    }

    private FinishCallCommunication(response: ServerEvent, store: PatientStore): void {
        let communicationsModel: IPatientCommunicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        communicationsModel = this.clearCallCommunicationFields(communicationsModel);
        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;

        overlayStore.hideOverlay("global-overlay");

        store.ActionsHistory.LoadPatientCommunications(store.PatientModels[response.PatientId].InactivableResponse.Id);
    }

    private clearCallCommunicationFields(communicationsModel: IPatientCommunicationsModel): IPatientCommunicationsModel {
        const clearDate = new Date();
        return communicationsModel
            .set("Note", "")
            .set("ShowNewCommunicationPopup", false)
            .set("PatientCommunicationHistoryId", null)
            .set("ShowEditMode", false)
            .set("SelectedClinician", null)
            .set("CallStartDateTime", clearDate)
            .set("CallEndTime", clearDate.addMinutes(5))
            .set("CallReasons", null);
    }

    private FinishCreateNewCommunication(response: ServerEvent, store: PatientStore): void {
        var communicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        communicationsModel = communicationsModel
            .set("ShowNewCommunicationPopup", false)
            .set("Note", "");
        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;
        sessionStore.hideGlobalOverlay();

        store.ActionsHistory.LoadPatientCommunications(store.PatientModels[response.PatientId].InactivableResponse.Id);
    }

    private LoadPatientRecalls(response: ServerEvent, store: PatientStore): void {
        let recallsModel = store.PatientHistoryRecallsModels[response.PatientId];
        let selectedRecallId = recallsModel.get("SelectedRecallId");

        let recalls = response.Data.RecallsList && response.Data.RecallsList
            .map(n => {
                let employee = cacheStore.GetCliniciansPersonsByContactId(n.CreatorContactId);
                let employeeResponsible = cacheStore.GetCliniciansPersonsByContactId(n.DoctorContactId);

                let itemModel = new PatientRecallsItem();
                itemModel.RecallId = n.RecallId;
                itemModel.IsCompleted = n.IsCompleted;
                itemModel.Type = n.Type;
                itemModel.Notes = n.Notes;
                itemModel.DueDate = moment(n.DueDate).format("YYYY/MM/DD");
                itemModel.CreatedBy = employee && employee.NameWithPrefix;
                itemModel.To = employeeResponsible && employeeResponsible.NameWithPrefix;
                itemModel.Specialization = n.SpecializationLookupId && cacheStore.GetLocalizedValueByLookupId(n.SpecializationLookupId);
                itemModel.Priority = cacheStore.GetLocalizedValueByLookupId(n.PriorityLookupId);

                return new PatientRecallsListItem(itemModel);
            }) as PatientRecallsListItem[] || [];

        recallsModel = recallsModel.set("RecallsList", recalls.toImmutable());

        store.PatientHistoryRecallsModels[response.PatientId] = recallsModel;

        if (selectedRecallId)
            store.ActionsHistory.LoadRecallCommunication(selectedRecallId);
    }

    private LoadRecallCommunication(response: ServerEvent, store: PatientStore): void {
        let recallsModel = store.PatientHistoryRecallsModels[response.PatientId];
        const recallCommunicationList = response.Data.RecallCommunicationList;

        if (recallCommunicationList) {
            let listItem = recallCommunicationList.map(n => {
                let itemModel: RecallCommunicationItem = new RecallCommunicationItem();
                itemModel.Id = n.Id;

                let employee = cacheStore.GetEmployeeByContactIdOrEmpty(n.ContactId);
                let employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
                itemModel.Employee = employeeTitle + " " + employee.LastName + ", " + employee.FirstName;

                itemModel.Notes = n.Notes;
                itemModel.State = cacheStore.GetLocalizedValueByLookupId(n.StateLookupId);

                let date = moment(n.DateCreated).format("YYYY-MM-DD");
                let time = moment(n.DateCreated).format("hh:mm:ss a");
                itemModel.DateCreated = date + " " + cacheStore.GetString("AtLabel") + " " + time;

                let listItem: RecallCommunicationListItem = new RecallCommunicationListItem(itemModel);
                return listItem;
            });
            recallsModel = recallsModel.set("RecallCommunication", Immutable.List<RecallCommunicationListItem>(listItem));
            recallsModel = recallsModel.set("SelectedStatusId", recallCommunicationList[0].StateLookupId);
        }
        else {
            recallsModel = recallsModel.set("RecallCommunication", Immutable.List<RecallCommunicationListItem>([]));
        }
        store.PatientHistoryRecallsModels[response.PatientId] = recallsModel;
    }

    private FinishMarkRecallAsCompleted(response: ServerEvent, store: PatientStore): void {
        var recallModel = store.PatientHistoryRecallsModels[response.PatientId];
        recallModel = recallModel.set("ShowNewEntryPopup", false);

        recallModel = recallModel.set("IsCompleted", true);
        store.PatientHistoryRecallsModels[response.PatientId] = recallModel;

        if (recallModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientRecall(response.PatientId, recallModel.get("SearchText"), recallModel.get("ShowOnlyNotCompleted"), recallModel.get("DateFrom"), recallModel.get("DateTo"));
        else
            store.ActionsHistory.LoadPatientRecall(response.PatientId, recallModel.get("SearchText"), recallModel.get("ShowOnlyNotCompleted"), null, null);
    }

    private CompleteInvalidationBill(response: ServerEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        privateBillModel = privateBillModel.set("IsInvalidPopupOpen", false);
        privateBillModel = privateBillModel.set("IsSave", true);

        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;

        store.ActionsHistory.LoadPrivateBillInfo(response.Data.PrivateBillId);

        if (privateBillModel.get("IsCriteriaExpanded") === true) {
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
        }
        else {
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), null, null);
        }
    }

    private LoadPatientPrivateBill(response: ServerEvent, store: PatientStore): void {
        if (response.Data.PrivateBillList) {
            const responseList: PatientPrivateBillItemResponse[] = response.Data.PrivateBillList;

            let listItems = responseList.map(n => {
                let itemModel = new PatientPrivateBillItem({
                    PrivateBillId: n.PrivateBillId,
                    BillNumber: n.BillNumber,
                    BillDate: moment(n.BillDate).format("YYYY/MM/DD"),
                    SharedItem: n.SharedItem,
                    SharedItemMessages: n.SharedItemMessages,
                    SiteId: n.SiteId,
                    BilledTo: n.BilledTo,
                    RecipientName: n.RecipientName,
                    IsLockedForEditing: n.IsLockedForEditing,
                    LastUpdateDate: n.LastUpdateDate,
                    PaymentMethod: n.PaymentMethod
                });

                const dateStart = moment(n.BillDate).format("dddd, MMMM DD, YYYY");
                const timeStart = moment(n.BillDate).format("HH:mm:ss");

                itemModel.BillDateForHeader = dateStart + " " + cacheStore.GetString("AtLabel") + " " + timeStart;

                itemModel.IsInvalid = n.IsInvalid;

                const employeeCreatedBy = cacheStore.GetEmployeeByContactFacetId(n.CreateByEmployeeId);
                const employeeTitleCreatedBy = cacheStore.GetLocalizedValueByLookupId(employeeCreatedBy.PrefixLookupId);
                itemModel.CreatedBy = employeeTitleCreatedBy + " " + employeeCreatedBy.LastName + ", " + employeeCreatedBy.FirstName;

                const employee = cacheStore.GetEmployeeByContactFacetId(n.EmployeeId);
                if (!!employee) {
                    const employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
                    itemModel.Employee = employeeTitle + " " + employee.LastName + ", " + employee.FirstName;
                    itemModel.EmployeeContactFacetId = n.EmployeeId;
                }

                itemModel.TotalBill = n.TotalBill;
                itemModel.IsClosed = n.IsClosed;
                itemModel.IsLockedForEditing = n.IsLockedForEditing;
                itemModel.CanModifyPaymentInformation = n.CanModifyPaymentInformation;

                return new PatientPrivateBillListItem(itemModel);
            });

            let privateBillModel = store.PatientHistoryBillsModels[response.PatientId];
            privateBillModel = privateBillModel
                .set("PrivateBillList", Immutable.List<PatientPrivateBillListItem>(listItems))
                .set("PortalLinkedItemModel", Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()));
            store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;

            if (!privateBillModel.get("IsNewBill"))
                if (!IsNullOrWhiteSpace(privateBillModel.get("SelectedPrivateBillId")))
                    store.ActionsHistory.LoadPrivateBillInfo(privateBillModel.get("SelectedPrivateBillId"));
                else if (listItems.length > 0) {
                    privateBillModel = privateBillModel.set("SelectedPrivateBillId", listItems.firstOrDefault().Model.PrivateBillId);
                    store.ActionsHistory.LoadPrivateBillInfo(privateBillModel.get("SelectedPrivateBillId"));
                }
        }
        else {
            let privateBillModel = store.PatientHistoryBillsModels[response.PatientId];
            privateBillModel = privateBillModel
                .set("PrivateBillList", Immutable.List<PatientPrivateBillListItem>([]))
                .set("PortalLinkedItemModel", Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()));
            store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
        }
    }

    private GeneratePatientPrivateBillPDF(response: ServerEvent, store: PatientStore): void {
        const fileName = response.Data["FileName"];
        window.open(apiSettings.buildAppUrl(`api/appointments/fetch/${fileName}.pdf`, { auth: true }), "_blank");

        let privateBillModel = store.PatientHistoryBillsModels[response.PatientId];
        privateBillModel = privateBillModel.set("IsPrinting", false);
        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
    }

    private GenerateAppointmentsHistoryPDF(response: ServerEvent): void {
        const fileName = response.Data["FileName"];
        window.open(apiSettings.buildAppUrl(`api/appointments/fetch/${fileName}`, { auth: true }), "_blank");
    }

    private FinalizePrivateBillSave(response: ServerEvent, store: PatientStore): void {
        let privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        if (response.Data.Success) {
            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
            privateBillModel = privateBillModel.set("IsNewBill", false);

            privateBillModel = privateBillModel.set("PrivateBillId", response.Data.NewPrivatilleId);
            privateBillModel = privateBillModel.set("SelectedPrivateBillId", response.Data.NewPrivatilleId);

            let dateVisit = moment(response.Data.DateOfVisit).format("YYYY/MM/DD");
            let timeVisit = moment(response.Data.DateOfVisit).format("HH:mm:ss");
            privateBillModel = privateBillModel.set("BillDate", dateVisit + " " + cacheStore.GetString("AtLabel") + " " + timeVisit);

            let dateStart = moment(response.Data.BillDate).format("dddd, MMMM DD, YYYY");
            let timeStart = moment(response.Data.BillDate).format("HH:mm:ss");
            let BillDateForHeader = dateStart + " " + cacheStore.GetString("AtLabel") + " " + timeStart;
            privateBillModel = privateBillModel.set("BillDateForHeader", BillDateForHeader);

            let employeeCreatedBy = cacheStore.GetEmployeeByContactFacetId(response.Data.CreatedById);
            let employeeTitleBilledBy = cacheStore.GetLocalizedValueByLookupId(employeeCreatedBy.PrefixLookupId);

            privateBillModel = privateBillModel.set("CreatedBy", employeeTitleBilledBy + " " + employeeCreatedBy.LastName + ", " + employeeCreatedBy.FirstName);

            if (!!response.Data.ProviderId) {
                const employeeProviderBy = cacheStore.GetEmployeeByContactFacetId(response.Data.ProviderId);
                const employeeTitleProviderBy = cacheStore.GetLocalizedValueByLookupId(employeeProviderBy.PrefixLookupId);
                privateBillModel = privateBillModel.set("Provider", employeeTitleProviderBy + " " + employeeProviderBy.LastName + ", " + employeeProviderBy.FirstName);
                privateBillModel = privateBillModel.set("EmployeeContactFacetId", response.Data.ProviderId);
            }

            privateBillModel = privateBillModel.set("BillNumber", response.Data.BillNumber);

            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", false);
            privateBillModel = privateBillModel.set("IsNewBill", false);
            privateBillModel = privateBillModel.set("IsSaveLocked", false);
            privateBillModel = privateBillModel.set("IsEditBill", false);
            privateBillModel = privateBillModel.set("IsInvalid", false);

            store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
            store.ActionsHistory.LoadPrivateBillInfo(response.Data.NewPrivatilleId);

            if (privateBillModel.get("IsCriteriaExpanded") === true)
                store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
            else
                store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), null, null);
        }
        else {
            if (response.Errors && response.Errors.length > 0) {
                // set proper localized message if error has a translation label code
                response.Errors.forEach(x => {
                    if (!IsNullOrWhiteSpace(x.Code))
                        x.Message = cacheStore.GetString(x.Code);
                });
                sessionStore.showPopup(response.Errors);

                // reload private bill to display last version
                sessionStore.dispatch((LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill));

                if (response.Data.SavePrivateBill != null) {
                    // reload private bill list to display last status (e.g. closed)
                    store.ActionsHistory.LoadPrivateBillInfo(response.Data.SavePrivateBill.Id);
                }

                privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", false);
                privateBillModel = privateBillModel.set("IsNewBill", false);
                privateBillModel = privateBillModel.set("IsSaveLocked", false);
                privateBillModel = privateBillModel.set("IsEditBill", false);
                store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
            }
        }
    }

    private FinalizePrivateBillSave_PortalWorkFlow(response: ServerEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        this.FinalizePrivateBillSave(response, store);

        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");

        portalStatusModel = portalStatusModel.set("LinkedItemModel", response.Data.SavedPrivateBill);
        portalStatusModel = portalStatusModel.set("LinkedItemId", response.Data.NewPrivatilleId);
        portalStatusModel = portalStatusModel.set("LinkedItemId", response.Data.NewPrivatilleId);

        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);
        privateBillModel = privateBillModel.set("IsPortalSyncPopupOpen", false);
        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;

        var operation = new LocalEvent(LocalPatientOperations.History.PrivateBill_SendBillToPortal);
        operation.AddItem("messageText", portalStatusModel.get("NewMessageText"));
        operation.AddItem("privateBillId", response.Data.NewPrivatilleId);
        LocalEventDispatcher.dispatch(operation);
    }

    private GetSelectedBillInformation(response: ServerEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        var data: PatientSelectedBillInfoResponse = new PatientSelectedBillInfoResponse();

        data.PrivateBillId = response.Data["PrivateBillId"];
        data.PrivateBillInfo = response.Data["PrivateBillInfo"];
        data.BilledService = response.Data["BilledService"];

        if (response.Data.PrivateBillInfo) {
            var privateBillInfo = data.PrivateBillInfo;

            privateBillModel = privateBillModel.set("SelectedPrivateBillId", data.PrivateBillId);
            privateBillModel = privateBillModel.set("PrivateBillId", data.PrivateBillId);
            privateBillModel = privateBillModel.set("BillNumber", privateBillInfo.Number);

            let employeeCreatedBy = cacheStore.GetEmployeeByContactFacetId(privateBillInfo.CreateByEmployeeId);
            let employeeTitleBilledBy = cacheStore.GetLocalizedValueByLookupId(employeeCreatedBy.PrefixLookupId);
            privateBillModel = privateBillModel.set("CreatedBy", employeeTitleBilledBy + " " + employeeCreatedBy.LastName + ", " + employeeCreatedBy.FirstName);

            let employeeProviderBy = cacheStore.GetEmployeeByContactFacetId(privateBillInfo.EmployeeId);
            if (!!employeeProviderBy) {
                let employeeTitleProviderBy = cacheStore.GetLocalizedValueByLookupId(employeeProviderBy.PrefixLookupId);
                privateBillModel = privateBillModel.set("Provider", employeeTitleProviderBy + " " + employeeProviderBy.LastName + ", " + employeeProviderBy.FirstName);
                privateBillModel = privateBillModel.set("EmployeeContactFacetId", employeeProviderBy.ContactFacet_id);
            }
            else {
                privateBillModel = privateBillModel.set("Provider", null);
                privateBillModel = privateBillModel.set("EmployeeContactFacetId", null);
            }

            var dateVisit = moment(privateBillInfo.VisitDate).format("YYYY/MM/DD");
            var timeVisit = moment(privateBillInfo.VisitDate).format("HH:mm:ss");
            privateBillModel = privateBillModel.set("BillDate", dateVisit + " " + cacheStore.GetString("AtLabel") + " " + timeVisit);
            privateBillModel = privateBillModel.set("BillDateRaw", privateBillInfo.VisitDate);
            privateBillModel = privateBillModel.set("DateOfVisit", privateBillInfo.VisitDate);

            var site = cacheStore.GetSiteById(privateBillInfo.SiteId);

            privateBillModel = privateBillModel.set("SiteName", site != null ? site.Model.ClinicalName : "");
            privateBillModel = privateBillModel.set("SelectedBillSiteId", privateBillInfo.SiteId);

            privateBillModel = privateBillModel.set("Notes", privateBillInfo.Notes);
            privateBillModel = privateBillModel.set("IsClosed", privateBillInfo.IsClosed);
            privateBillModel = privateBillModel.set("IsLockedForEditing", privateBillInfo.IsLockedForEditing);
            privateBillModel = privateBillModel.set("CanModifyPaymentInformation", privateBillInfo.CanModifyPaymentInformation);

            const paymentMethodLabel = BillingPaymentMethodsHelper.getPaymentMethodLabelWithId(privateBillInfo.PaymentMethod);
            privateBillModel = privateBillModel.set("PaymentMethod", paymentMethodLabel);

            privateBillModel = privateBillModel.set("Total", Math.round((privateBillInfo.Total + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("QSTTotal", Math.round((privateBillInfo.TotalQST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("GSTTotal", Math.round((privateBillInfo.TotalGST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("HSTTotal", Math.round((privateBillInfo.TotalHST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("GenericTaxTotal", Math.round((privateBillInfo.TotalGenericTax + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("Discount", Math.round((privateBillInfo.Discount + 0.00001) * 100) / 100);

            // the discounts on the subtotal are calculatd in the back-end so we don't have to do calculate them everytime in medicalNote, history and billingTab
            privateBillModel = privateBillModel.set("Subtotal", Math.round((privateBillInfo.Subtotal + 0.00001) * 100) / 100);

            privateBillModel = privateBillModel.set("QST", Math.round((privateBillInfo.QST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("GST", Math.round((privateBillInfo.GST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("HST", Math.round((privateBillInfo.HST + 0.00001) * 100) / 100);
            privateBillModel = privateBillModel.set("GenericTax", Math.round((privateBillInfo.GenericTax + 0.00001) * 100) / 100);

            const formattedDate = LuxonDateTimeParser.parse(privateBillInfo.InvalidationDate)?.toFormat("EEEE',' MMMM d',' yyyy", { locale: sessionStore.locale }) ?? "";
            privateBillModel = privateBillModel.set("InvalidDate", formattedDate);

            const employeeInvalidatedBy = cacheStore.GetEmployeeByContactFacetId(privateBillInfo.InvalidatedByEmployeeId);
            if (!!employeeInvalidatedBy) {
                const employeeTitleInvalidatedBy = cacheStore.GetLocalizedValueByLookupId(employeeInvalidatedBy.PrefixLookupId);
                privateBillModel = privateBillModel.set("InvalidatedBy", `${employeeTitleInvalidatedBy} ${employeeInvalidatedBy.LastName}, ${employeeInvalidatedBy.FirstName}`);
            }
            privateBillModel = privateBillModel
                .set("InvalidNote", privateBillInfo.InvalidationNote)
                .set("IsInvalid", privateBillInfo.IsInvalid);

            if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
                const recipientDetails = new RecipientDetailsModel({
                    RecipientName: privateBillInfo.RecipientName,
                    RecipientAddress: privateBillInfo.RecipientAddress,
                    RecipientPhoneNumber: privateBillInfo.RecipientPhoneNumber,
                    RecipientFaxNumber: privateBillInfo.RecipientFaxNumber
                });
                privateBillModel = privateBillModel.set("SelectedBillRecipientDetails", recipientDetails);
                privateBillModel = privateBillModel.set("RecipientDetails", recipientDetails);
                privateBillModel = privateBillModel.set("BilledTo", privateBillInfo.BilledTo);
            }
        }

        privateBillModel = privateBillModel.set("CanInvalidateBill", true);

        var totalAmount = 0;
        if (data.BilledService != null) {
            var TableItems = data.BilledService.map(n => {
                var itemModel: BilledServiceItem = new BilledServiceItem();

                itemModel.ServiceMultiplicator = n.Multiplicator;
                itemModel.ServiceName = itemModel.LoadServiceName(n.NameEs, n.NameFr, n.NameEs);

                itemModel.GST = n.GST;
                itemModel.QST = n.QST;
                itemModel.HST = n.HST;
                itemModel.GenericTax = n.GenericTax;
                itemModel.HasGST = n.HadGST;
                itemModel.HasQST = n.HadQST;
                itemModel.HasHST = n.HadHST;
                itemModel.HasGenericTax = n.HadGenericTax;
                itemModel.CostCenterId = n.CostCenterId;

                if (n.QuantityPaid > 0) {
                    privateBillModel = privateBillModel.set("CanInvalidateBill", false);
                }

                if (n.ServiceNumber)
                    itemModel.ServiceNumber = n.ServiceNumber;
                else
                    itemModel.ServiceNumber = "N/A";

                itemModel.ServicePrice = n.Price;

                totalAmount += n.Price * n.Multiplicator;

                return itemModel;
            });
            privateBillModel = privateBillModel.set("BilledService", Immutable.List<BilledServiceItem>(TableItems));
        }

        privateBillModel = privateBillModel.set("TotalAmount", Math.round((totalAmount + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
        privateBillModel = privateBillModel.set("IsEditBill", false);
        privateBillModel = privateBillModel.set("IsNewBill", false);

        if (privateBillModel.get("IsSave") === false && !privateBillModel.get("IsPrinting")) {
            privateBillModel = privateBillModel
                .set("IsPrinting", true)
                .set("IsSave", true);
            store.ActionsHistory.PrintPrivateBill(privateBillModel.get("SelectedPrivateBillId"));
        }

        switch (privateBillInfo?.DiscountType) {
            case 0:
                privateBillModel = privateBillModel.set("DiscountType", "$");
                break;

            case 1:
                privateBillModel = privateBillModel.set("DiscountType", "%");
                break;

            default:
                break;
        }

        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
    }

    private showPdfPrintPreview(response: ServerEvent): void {
        PrintHelper.ShowPrintPreviewFileName(response.Data["FileName"]);
    }

    private sendPrivateBillToPortal(response: ServerEvent, store: PatientStore): void {
        let privateBillModel = store.PatientHistoryBillsModels[response.PatientId];
        let portalSyncModel = privateBillModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            portalSyncModel = portalSyncModel.set("ErrorMessage", response.Errors[0].Message);
        }
        else {
            let objectId = response.Data["ObjectId"];
            let sharedItem = response.Data["SharedItem"];
            let sharedItemMessages = response.Data["Messages"];

            let bills = privateBillModel.get("PrivateBillList").toArray();
            bills.forEach(x => {
                if (x.Model.PrivateBillId == objectId) {
                    x.Model.SharedItem = sharedItem;
                    x.Model.SharedItemMessages = sharedItemMessages;
                }
            });
            privateBillModel = privateBillModel.set("PrivateBillList", bills.toImmutable());

            portalSyncModel = portalSyncModel.set("SharedItem", sharedItem);
            portalSyncModel = portalSyncModel.set("LinkedItemMessages", sharedItemMessages.toImmutable());

            privateBillModel = privateBillModel.set("IsPortalSyncPopupOpen", false);
            sessionStore.hideGlobalOverlay();
        }

        portalSyncModel = portalSyncModel.set("IsSending", false);
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalSyncModel);
        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
    }

    private sendCommunicationToPortal(response: ServerEvent, store: PatientStore): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        let portalSyncModel = communicationsModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            portalSyncModel = portalSyncModel.set("ErrorMessage", response.Errors[0].Message);
        }
        else {
            let objectId = response.Data["ObjectId"];
            let sharedItem = response.Data["SharedItem"];

            let communications = communicationsModel.get("CommunicationsList").toArray();
            communications.forEach(x => {
                if (x.Model.Id == objectId)
                    x.Model.SharedItem = sharedItem;
            });
            communicationsModel = communicationsModel.set("CommunicationsList", communications.toImmutable());

            communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", false);
            sessionStore.hideGlobalOverlay();
        }

        portalSyncModel = portalSyncModel.set("IsSending", false);
        communicationsModel = communicationsModel.set("PortalLinkedItemModel", portalSyncModel);
        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;
    }

    private FinishCloseAndDeleteSelectedBill(response: ServerEvent, store: PatientStore): void {
        var success: boolean = response.Data["Success"];
        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];

        if (!!success) {
            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", false);
            privateBillModel = privateBillModel.set("IsNewBill", false);
            privateBillModel = privateBillModel.set("DateOfVisit", null);
            privateBillModel = privateBillModel.set("SelectedClinicians", Immutable.fromJS([]));
            privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);
            privateBillModel = privateBillModel.set("SelectedPrivateBillId", null);

            if (privateBillModel.get("IsCriteriaExpanded") === true)
                store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
            else
                store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[response.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), null, null);
        }

        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;
    }

    private loadSharedItem(response: ServerEvent, store: PatientStore): void {
        var sharedItem: SharedItem = response.Data["SharedItem"];

        if (sharedItem && sharedItem.Messages) {
            sharedItem.Messages.map(x => {
                x.IsSelected = x.Status !== PortalSyncStatus.Revoked;
                x.tmpIdForUI = SharedItemMessage.getTempIdForUi(x);
            });
        }

        if (sharedItem.SharedItemType == PortalLinkedItemType.PrivateBill)
            this.loadSharedItemPrivateBill(response, store, sharedItem);

        if (sharedItem.SharedItemType == PortalLinkedItemType.CommunicationHistory)
            this.loadSharedItemCommunication(response, store, sharedItem);
    }

    private loadSharedItemPrivateBill(response: ServerEvent, store: PatientStore, sharedItem: SharedItem): void {
        var billModel = store.PatientHistoryBillsModels[response.PatientId];
        var portalStatusModel: IPortalLinkedItemModel = billModel.get("PortalLinkedItemModel");

        portalStatusModel = portalStatusModel.set("LinkedItemMessages", (sharedItem && sharedItem.Messages) ? sharedItem.Messages.toImmutable() : [].toImmutable());
        portalStatusModel = portalStatusModel.set("SharedItem", sharedItem);
        portalStatusModel = portalStatusModel.set("LinkedItemType", sharedItem.SharedItemType);
        portalStatusModel = portalStatusModel.set("IsSending", false);

        billModel = billModel.set("PortalLinkedItemModel", portalStatusModel);
        billModel = billModel.set("IsPortalSyncPopupOpen", true);

        store.PatientHistoryBillsModels[response.PatientId] = billModel;
    }

    private loadSharedItemCommunication(response: ServerEvent, store: PatientStore, sharedItem: SharedItem): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        let portalStatusModel = communicationsModel.get("PortalLinkedItemModel");

        portalStatusModel = portalStatusModel.set("LinkedItemMessages", (sharedItem && sharedItem.Messages) ? sharedItem.Messages.toImmutable() : [].toImmutable());
        portalStatusModel = portalStatusModel.set("SharedItem", sharedItem);
        portalStatusModel = portalStatusModel.set("LinkedItemType", sharedItem.SharedItemType);
        portalStatusModel = portalStatusModel.set("IsSending", false);

        communicationsModel = communicationsModel.set("PortalLinkedItemModel", portalStatusModel);
        communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", true);

        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;
    }

    private revokePrivateBill(response: ServerEvent, store: PatientStore): void {
        var privateBillId = response.Data["ObjectId"];
        var sharedItem = response.Data["SharedItem"];

        // reset portalmessages & sync statuses in privatebill model model (with adjusted status)
        var privateBillModel = store.PatientHistoryBillsModels[response.PatientId];
        var bills = privateBillModel.get("PrivateBillList").toArray();
        var portalStatusPrivateBillModel = privateBillModel.get("PortalLinkedItemModel");
        var itemLink = portalStatusPrivateBillModel.get("LinkedItemModel");
        if (itemLink != null && !IsNullOrWhiteSpace(itemLink.getSharedItemId())) {
            var currentBill: PatientPrivateBillListItem = ArrayHelper.Find(bills, x => x.Model.PrivateBillId == privateBillId);
            if (currentBill != null) {
                currentBill.Model.SharedItem = sharedItem;

                var idx = currentBill != null ? bills.indexOf(currentBill) : -1;
                bills[idx] = currentBill;
                privateBillModel = privateBillModel.set("PrivateBillList", Immutable.List(bills));
            }
        }

        var portalSyncModel = privateBillModel.get("PortalLinkedItemModel");
        portalSyncModel = portalSyncModel.set("SharedItem", sharedItem);
        portalSyncModel = portalSyncModel.set("IsSending", false);
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalSyncModel);

        privateBillModel = privateBillModel.set("IsPortalSyncPopupOpen", false);

        store.PatientHistoryBillsModels[response.PatientId] = privateBillModel;

        sessionStore.hideGlobalOverlay();
    }

    private revokeCommunication(response: ServerEvent, store: PatientStore): void {
        var communicationId = response.Data["ObjectId"];
        var sharedItem = response.Data["SharedItem"];

        // reset portalmessages & sync statuses in communication model model
        var communicationsModel = store.PatientHistoryCommunicationsModels[response.PatientId];
        var communications = communicationsModel.get("CommunicationsList").toArray();
        var currentCommunication: PatientCommunicationsListItem = ArrayHelper.Find(communications, x => x.Model.Id == communicationId);
        if (currentCommunication != null) {
            currentCommunication.Model.SharedItem = sharedItem;

            var idx = currentCommunication != null ? communications.indexOf(currentCommunication) : -1;
            communications[idx] = currentCommunication;
            communicationsModel = communicationsModel.set("CommunicationsList", Immutable.List(communications));
        }

        var portalSyncModel = communicationsModel.get("PortalLinkedItemModel");
        portalSyncModel = portalSyncModel.set("SharedItem", sharedItem);
        portalSyncModel = portalSyncModel.set("IsSending", false);
        communicationsModel = communicationsModel.set("PortalLinkedItemModel", portalSyncModel);

        communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", false);

        store.PatientHistoryCommunicationsModels[response.PatientId] = communicationsModel;

        sessionStore.hideGlobalOverlay();
    }

    private getSingleTask(response: ServerEvent, store: PatientStore): void {
        let task: PatientTaskItem = response.Data["Task"];

        if (task) {
            let taskModel = store.PatientHistoryTasksModels[response.PatientId];

            taskModel = taskModel.set("ShowDetails", true);
            taskModel = taskModel.set("IM_ID", task.IndividualMessageId);
            taskModel = taskModel.set("FromEmployee", Employee.getFullNameWithPrefix(task.FromEmployee));
            taskModel = taskModel.set("Subject", task.Subject);
            taskModel = taskModel.set("Body", task.Body);
            taskModel = taskModel.set("ToEmployeeWithStatus", task.ToEmployees);
            taskModel = taskModel.set("GenericTaskStatusId", task.OverallTaskStatusLookupId);

            if (task.StartDate) {
                let formattedStartDate = moment(task.StartDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(task.StartDate).format("HH:mm:ss");
                taskModel = taskModel.set("StartDate", formattedStartDate);
            }
            else
                taskModel = taskModel.set("StartDate", "");

            if (task.DueDate) {
                let formattedDueDate = moment(task.DueDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(task.DueDate).format("HH:mm:ss");
                taskModel = taskModel.set("DueDate", formattedDueDate);
            }
            else
                taskModel = taskModel.set("DueDate", "");

            taskModel = taskModel.set("Priority", task.Priority);
            taskModel = taskModel.set("Type", task.Type);

            store.PatientHistoryTasksModels[response.PatientId] = taskModel;

            store.ActionsHistory.LoadMessageOrTaskNotes(task.IndividualMessageId, SubServerOperations.PatientHistory.LoadTaskNotes);
        }
    }

    private getSingleMessage(response: ServerEvent, store: PatientStore): void {
        let message: PatientMessageItem = response.Data["Message"];
        let toEmployees: IndividualDTO[] = response.Data["ToEmployees"];

        if (message) {
            let messageModel = store.PatientHistoryMessagesModels[response.PatientId];

            let toEmployeesString = "";
            toEmployees.forEach((e, idx) => {
                toEmployeesString +=
                    cacheStore.GetLocalizedValueByLookupId(e.PrefixLookupId)
                    + " "
                    + e.LastName
                    + ", "
                    + e.FirstName
                    + (idx === toEmployees.lastIndexOf(e) ? " " : " ; ");
            });

            messageModel = messageModel
                .set("ShowDetails", true)
                .set("SelectedMessageId", message.IndividualMessageId)
                .set("SelectedMessageFrom", Employee.getFullNameWithPrefix(message.FromEmployee))
                .set("SelectedMessageSubject", message.Subject)
                .set("SelectedMessageBody", message.Body)
                .set("SelectedMessageTo", toEmployeesString);

            store.PatientHistoryMessagesModels[response.PatientId] = messageModel;

            store.ActionsHistory.LoadMessageOrTaskNotes(
                message.IndividualMessageId,
                SubServerOperations.PatientHistory.LoadMessageNotes
            );
        }
    }

    private getSingleRecall(response: ServerEvent, store: PatientStore): void {
        let recall = response.Data["Recall"];

        if (recall) {
            let recallModel = store.PatientHistoryRecallsModels[response.PatientId];

            recallModel = recallModel
                .set("ShowDetails", true)
                .set("SelectedRecallId", recall.Id)
                .set("IsCompleted", recall.IsCompleted)
                .set("Description", recall.Type)
                .set("Provider", Employee.getFullNameWithPrefix(recall.DoctorContactId))
                .set("Speciality", cacheStore.GetLocalizedValueByLookupId(recall.SpecializationLookupId))
                .set("DueDate", moment(recall.DueDate).format("YYYY/MM/DD"))
                .set("CreatedBy", Employee.getFullNameWithPrefix(recall.CreatorContactId))
                .set("Priority", cacheStore.GetLocalizedValueByLookupId(recall.PriorityLookupId))
                .set("Notes", recall.Notes);

            store.PatientHistoryRecallsModels[response.PatientId] = recallModel;

            store.ActionsHistory.LoadRecallCommunication(recall.Id);
        }
    }
}

export { PatientHistoryResponseHandler };
